import { io, Socket } from 'socket.io-client';
import { CustomButton } from '../types';
import { config } from '../config';

/**
 * Socket.IO service for managing real-time counter updates
 */
class SocketService {
  private socket: Socket | null = null;
  private connectedCounters: Set<string> = new Set();
  private eventHandlers: Map<string, Set<Function>> = new Map();
  private reconnecting: boolean = false;

  /**
   * Initialize the Socket.IO connection
   */
  connect(): Socket {
    if (!this.socket) {
      this.socket = io(config.api.baseUrl, {
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
      });

      this.setupConnectionEvents();
    }

    return this.socket;
  }

  /**
   * Disconnect the Socket.IO connection
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connectedCounters.clear();
    }
  }

  /**
   * Subscribe to a counter's updates
   * @param counterId The counter ID to subscribe to
   */
  subscribeToCounter(counterId: string): void {
    if (!this.socket) {
      this.connect();
    }

    if (this.socket && !this.connectedCounters.has(counterId)) {
      this.socket.emit('subscribeToCounter', { counterId });
      this.connectedCounters.add(counterId);
    }
  }

  /**
   * Unsubscribe from a counter's updates
   * @param counterId The counter ID to unsubscribe from
   */
  unsubscribeFromCounter(counterId: string): void {
    if (this.socket && this.connectedCounters.has(counterId)) {
      this.socket.emit('unsubscribeFromCounter', { counterId });
      this.connectedCounters.delete(counterId);
    }
  }

  /**
   * Register an event handler
   * @param event The event name
   * @param handler The event handler function
   */
  on(event: string, handler: Function): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    
    this.eventHandlers.get(event)?.add(handler);
    
    if (this.socket) {
      this.socket.on(event, (...args) => {
        handler(...args);
      });
    }
  }

  /**
   * Remove an event handler
   * @param event The event name
   * @param handler The event handler function to remove
   */
  off(event: string, handler: Function): void {
    if (this.eventHandlers.has(event)) {
      this.eventHandlers.get(event)?.delete(handler);
    }
    
    if (this.socket) {
      this.socket.off(event);
      
      // Re-register the remaining handlers
      this.eventHandlers.get(event)?.forEach(h => {
        this.socket?.on(event, (...args) => h(...args));
      });
    }
  }

  /**
   * Emit a button update event to the server
   * @param buttonId The button ID that was updated
   */
  emitButtonUpdate(buttonId: string): void {
    if (!this.socket || !this.socket.connected) {
      return;
    }
    
    this.socket.emit('buttonUpdate', { buttonId });
  }

  /**
   * Set up connection events for the socket
   */
  private setupConnectionEvents(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      // Re-subscribe to counters on reconnection
      if (this.reconnecting) {
        this.connectedCounters.forEach(counterId => {
          this.socket?.emit('subscribeToCounter', { counterId });
        });
        this.reconnecting = false;
      }
      
      // Re-register all event handlers
      this.eventHandlers.forEach((handlers, event) => {
        handlers.forEach(handler => {
          this.socket?.on(event, (...args) => {
            handler(...args);
          });
        });
      });
    });

    this.socket.on('disconnect', () => {
      this.reconnecting = true;
    });

    this.socket.on('error', (error) => {
      // Silent fail in production
    });
  }
}

// Create a singleton instance
const socketService = new SocketService();

export default socketService; 